import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import {
  DirtyFormAlert,
  Label,
  Modal,
  Notification,
  Selector,
  Spinner,
  TextInput,
} from 'shared/components';
import {
  validateRequiredValue,
  debouncedValidateRequiredValue,
} from 'shared/validation';
import {
  createLicenseCustomField,
  updateLicenseCustomField,
} from 'licenses/LicenseContainer/CustomFields/actions';
import { useTranslation } from 'react-i18next';

const CustomFieldForm = ({ closeCb, field, license, refetchCustomFields }) => {
  const { t } = useTranslation();
  const licenseID = get(license, 'id');
  const [dirty, setDirty] = useState(false);
  const [isDirtyFormAlertDisplayed, setDirtyFormDisplay] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const customFieldEnums = get(field, 'product_custom_field_enums', []);
  let enumOptions = customFieldEnums.map((enumOption) => ({
    label: enumOption.value,
    value: enumOption.value,
  }));
  if (customFieldEnums.length > 0) {
    const defaultValue = get(field, 'default_value');
    if (
      defaultValue &&
      !customFieldEnums.some((e) => e.value === defaultValue)
    ) {
      enumOptions.push({ label: defaultValue, value: defaultValue });
    }
  }
  const [fieldValue, setFieldValue] = useState(
    get(field, 'value') || get(field, 'default_value', ''),
  );
  const [fieldValueError, setFieldValueError] = useState('');

  const validateValue = async (val, cb) => {
    if (val === '' || val === null || val === undefined) {
      cb('');
      return true;
    }

    setLoading(true);
    let errors;
    try {
      errors = await validateRequiredValue(val);
      cb(errors);
    } catch (err) {}
    setLoading(false);
    if (errors) {
      return false;
    }
    return true;
  };

  const handleClose = () => {
    if (!dirty) {
      return closeCb();
    }
    return setDirtyFormDisplay(true);
  };

  const isFormValid = async () => {
    const isValueValid = await validateValue(fieldValue, setFieldValueError);
    return isValueValid;
  };

  const patchLicenseField = () => {
    const fieldID = get(field, 'license_custom_field_id');
    const data = {
      value: fieldValue,
      license: licenseID,
      product_custom_field: get(field, 'product_custom_field'),
    };

    updateLicenseCustomField(fieldID, data)
      .then(() => {
        Notification(
          'success',
          t('Changes saved successfully'),
          t('Custom field edited'),
        );
        refetchCustomFields();
        handleClose();
      })
      .catch((err) => {
        setLoading(false);
        Notification(
          'error',
          t('Your changes were not saved'),
          t('There was an error while saving your changes'),
        );
      });
  };

  const postLicenseField = async () => {
    const fieldID = get(field, 'product_custom_field') || get(field, 'id');
    const data = {
      value: fieldValue,
      license: licenseID,
      product_custom_field: fieldID,
    };

    createLicenseCustomField(data)
      .then(() => {
        Notification(
          'success',
          t('Changes saved successfully'),
          t('Custom field edited'),
        );
        refetchCustomFields();
        handleClose();
      })
      .catch((err) => {
        setLoading(false);
        Notification(
          'error',
          t('Your changes were not saved'),
          t('There was an error while saving your changes'),
        );
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = await isFormValid();
    if (!isValid || isLoading) {
      return false;
    }

    setDirty(false);
    setLoading(true);

    if (get(field, 'license_custom_field_id')) {
      // patch existing license custom field row
      patchLicenseField();
    } else {
      // post new license custom field created from editing product custom field
      postLicenseField();
    }
    return true;
  };

  return (
    <Modal
      confirmCb={handleSubmit}
      closeCb={handleClose}
      disabled={isLoading}
      title={t('Edit custom field')}
      size="sm"
    >
      <div className="CustomFieldForm">
        <form className="custom-field-form" onSubmit={handleSubmit}>
          <Label text={t('Name')} inputId="name" />
          <TextInput
            id="name"
            value={get(field, 'name', '')}
            handleChange={() => {}}
            disabled
          />
          <Label text={t('Value')} inputId="value" />
          {isLoading ? (
            <div className="custom-field-spinner">
              <Spinner size="sm" />
            </div>
          ) : enumOptions.length ? (
            <Selector
              id="value"
              value={fieldValue}
              options={enumOptions}
              disabled={isLoading}
              maxWidth="300px"
              handleChange={(val) => {
                setDirty(true);
                setFieldValue(val);
                setFieldValueError('');
              }}
              placeholder={t('Select value')}
            />
          ) : (
            <TextInput
              id="value"
              value={fieldValue}
              error={fieldValueError}
              handleChange={(val) => {
                setDirty(true);
                setFieldValue(val);
                if (val === '' || val === null || val === undefined) {
                  setFieldValueError('');
                  return;
                }
                debouncedValidateRequiredValue(val).then((err) =>
                  setFieldValueError(err),
                );
              }}
            />
          )}
          <button type="submit" style={{ visibility: 'hidden' }} />
        </form>
      </div>
      {isDirtyFormAlertDisplayed && (
        <DirtyFormAlert
          dirty={dirty}
          closeAlert={() => setDirtyFormDisplay(false)}
          closeCb={closeCb}
        />
      )}
    </Modal>
  );
};

CustomFieldForm.propTypes = {
  closeCb: PropTypes.func.isRequired,
  refetchCustomFields: PropTypes.func.isRequired,
  license: PropTypes.object.isRequired,
  field: PropTypes.object.isRequired,
};

export default CustomFieldForm;
